import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import capitalize from "lodash-es/capitalize";
import difference from "lodash-es/difference";
import isEmpty from "lodash-es/isEmpty";
import { useIntl } from "react-intl";

import Role from "@mapmycustomers/shared/enum/Role";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DashCard from "@app/component/DashCard";
import MoreButton from "@app/component/MoreButton";
import { DealsGroupByType } from "@app/scene/home/enum/DealsGroupByType";
import { DisplayType } from "@app/scene/home/enum/DisplayType";
import {
  availableDealsGroupByEntitiesSelector,
  getDealRottingStages,
  getDealsRotting,
  getDealsRottingFilter,
  isDealsRottingLoading,
} from "@app/scene/home/store";
import { fetchDealsRotting, setDealsRottingFilter } from "@app/scene/home/store/actions";
import { DealsGroupBy } from "@app/scene/home/types/DealGroupBy";
import { RecordsStats } from "@app/scene/home/types/RecordsStats";
import analytics from "@app/scene/reports/reportsAnalytics";
import { getFunnelStages } from "@app/store/deal";
import { getMe } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { ClassName } from "@app/types/ui/ClassName";
import defaultIdGetter from "@app/util/defaultIdGetter";

import Chart from "./Chart";
import EmptyRecordsStats from "./EmptyRecordsStats";
import FilterTypeSelect from "./FilterTypeSelect";
import GroupByModal from "./GroupByModal";
import { displayTypeMessages, generalMessages, groupByMessages } from "./messages";

interface Props {}

const DealsRottingCard: React.FC<
  Props & ClassName & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({
  className,
  fetchDealsRotting,
  filter,
  funnelStages,
  groupByEntities,
  isLoading,
  me,
  records: allRecords,
  setFilter,
  stagesRecords,
}) => {
  const intl = useIntl();

  const { displayType, groupByType } = filter;

  const [usersDialogVisible, showUsersDialog, hideUsersDialog] = useBoolean();

  const [groupByEntitiesKey, setGroupByEntitiesKey] = useState<string>("");
  const [selectableEntities, setSelectableEntities] = useState<DealsGroupBy[]>(groupByEntities);
  const [selectedEntities, setSelectedEntities] = useState<DealsGroupBy[]>(groupByEntities);
  const [hideOption, setHideOption] = useState<boolean>(false);
  const records = useMemo(
    () =>
      selectedEntities.reduce<Record<Identified["id"], RecordsStats>>((acc, { id }) => {
        acc[id] = allRecords[id];
        return acc;
      }, {}),
    [selectedEntities, allRecords]
  );

  const GroupByRecordsDialog = GroupByModal[groupByType];

  const handleShowGroupByEntitiesSelectDialog = useCallback(() => {
    showUsersDialog();
    analytics.clicked(["Rotting Deals", `Select ${capitalize(groupByType)}s`]);
  }, [groupByType, showUsersDialog]);

  // The GroupByRecordsDialog component caches selectedEntities on the first render.
  // But the relevant selectedEntities value is calculated after that in the following useEffect.
  // So we use this to reset and pass new relevant values and rerender the GroupByRecordsDialog component at the same moment.
  useEffect(() => {
    if (
      groupByEntities.length !== selectableEntities.length ||
      difference(groupByEntities, selectableEntities).length
    ) {
      setGroupByEntitiesKey(`${groupByType}${groupByEntities.map(defaultIdGetter).join("|")}`);
      setSelectableEntities(groupByEntities);
      setSelectedEntities(groupByEntities);
    }
  }, [
    groupByType,
    groupByEntities,
    selectableEntities,
    setGroupByEntitiesKey,
    setSelectableEntities,
  ]);

  useEffect(() => {
    fetchDealsRotting({ dealsGroupByType: groupByType });
  }, [fetchDealsRotting, groupByType]);

  useEffect(() => {
    if (groupByType === DealsGroupByType.USER || groupByType === DealsGroupByType.TEAM) {
      setHideOption(false);
    }
  }, [groupByType, setHideOption]);

  const toggleDisplayType = useCallback(
    () =>
      setFilter({
        ...filter,
        displayType: displayType === DisplayType.PERCENT ? DisplayType.GROSS : DisplayType.PERCENT,
      }),
    [displayType, filter, setFilter]
  );

  const setGroupByType = useCallback(
    (groupByType: DealsGroupByType) => {
      setFilter({ ...filter, groupByType });
      analytics.clicked(["Rotting Deals", `By ${capitalize(groupByType)}`]);
    },
    [filter, setFilter]
  );

  const groupByEnabled = groupByEntitiesKey.startsWith(groupByType);

  return (
    <DashCard
      actions={[
        <FilterTypeSelect
          hasTeamAccess={me.role.key === Role.OWNER || me.role.key === Role.MANAGER}
          key="type"
          onChange={setGroupByType}
          value={groupByType}
        />,
        <Dropdown
          key="menu"
          overlay={
            <Menu>
              {!hideOption && (
                <Menu.Item
                  disabled={!groupByEnabled}
                  onClick={handleShowGroupByEntitiesSelectDialog}
                >
                  {intl.formatMessage(groupByMessages[groupByType])}
                </Menu.Item>
              )}
              <Menu.Item disabled={hideOption === true} onClick={toggleDisplayType}>
                {hideOption === true ? (
                  <Tooltip
                    placement="bottom"
                    title={intl.formatMessage({
                      id: "home.dealsRotting.disabled.drilldown.state.displayType",
                      defaultMessage: "Go back to Funnel view to change display type",
                      description: "message for users to switch displayType",
                    })}
                  >
                    {intl.formatMessage(displayTypeMessages[displayType])}
                  </Tooltip>
                ) : (
                  intl.formatMessage(displayTypeMessages[displayType])
                )}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <MoreButton />
        </Dropdown>,
      ]}
      className={className}
      title={intl.formatMessage(generalMessages.title)}
    >
      {!isLoading ? (
        isEmpty(selectableEntities) ? (
          <EmptyRecordsStats />
        ) : (
          <Chart
            displayType={displayType}
            funnelStages={funnelStages}
            groupByEntities={selectableEntities}
            groupByType={groupByType}
            hideOption={setHideOption}
            records={records}
            stagesRecords={stagesRecords}
          />
        )
      ) : (
        <LoadingSpinner />
      )}

      {groupByEnabled && (
        <GroupByRecordsDialog
          entities={selectableEntities}
          key={groupByEntitiesKey}
          onConfirm={setSelectedEntities}
          onHide={hideUsersDialog}
          selectedEntities={selectedEntities}
          visible={usersDialogVisible}
        />
      )}
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  filter: getDealsRottingFilter(state),
  funnelStages: getFunnelStages(state),
  groupByEntities: availableDealsGroupByEntitiesSelector(state),
  isLoading: isDealsRottingLoading(state),
  me: getMe(state)!, // must be defined at this point
  records: getDealsRotting(state),
  stagesRecords: getDealRottingStages(state),
});

const mapDispatchToProps = {
  fetchDealsRotting: fetchDealsRotting.request,
  setFilter: setDealsRottingFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealsRottingCard);
